import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { FetchWrapperError } from '../../../../helpers';
import { PROVIDER_SSN_REVEAL_PASSWORD_MIN_LENGTH } from '../constants';
import { fetchProviderSsn, revealProviderSsn, updateProviderSsn } from '../services';

export function useProviderSsn(providerId: number | undefined) {
  const { getAccessTokenSilently } = useAuth0();
  const [maskedSsn, setMaskedSsn] = useState<string | null>(null);
  const [ssnLoaded, setSsnLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealPassword, setRevealPasswordValue] = useState('');
  const [revealedSsn, setRevealedSsn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setRevealPassword = (value: string) => {
    setRevealPasswordValue(value);
    setError(null);
  };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setMaskedSsn(null);
    setSsnLoaded(false);
    setEditOpen(false);
    setEditValue('');
    setRevealOpen(false);
    setRevealPassword('');
    setRevealedSsn(null);
    setError(null);

    if (providerId === undefined) {
      setLoading(false);
      return () => controller.abort();
    }

    setLoading(true);
    fetchProviderSsn(providerId, getAccessTokenSilently, controller.signal)
      .then((response) => {
        if (active) {
          setMaskedSsn(response.ssn);
          setSsnLoaded(true);
        }
      })
      .catch(() => {
        if (active && !controller.signal.aborted) {
          setError('Unable to load SSN');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [getAccessTokenSilently, providerId]);

  const openEdit = () => {
    setError(null);
    setEditValue('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditValue('');
    setError(null);
  };

  const save = async () => {
    if (providerId === undefined || !editValue.trim()) {
      setError('Enter a valid SSN');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      setMaskedSsn(
        (await updateProviderSsn(providerId, editValue, getAccessTokenSilently)).ssn,
      );
      closeEdit();
    } catch (error) {
      if (error instanceof FetchWrapperError) {
        setError(error.code === '403' ? 'SSN is already set' : error.message);
      } else {
        setError('Unable to save SSN');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openReveal = () => {
    setError(null);
    setRevealPassword('');
    setRevealedSsn(null);
    setRevealOpen(true);
  };

  const closeReveal = () => {
    setRevealOpen(false);
    setRevealPassword('');
    setRevealedSsn(null);
    setError(null);
  };

  const reveal = async () => {
    if (providerId === undefined || !revealPassword) {
      setError('Enter the password');
      return;
    }
    if (revealPassword.length < PROVIDER_SSN_REVEAL_PASSWORD_MIN_LENGTH) {
      setError('Password must be at least eight characters');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      setRevealedSsn(
        (await revealProviderSsn(providerId, revealPassword, getAccessTokenSilently)).ssn,
      );
      setRevealPassword('');
    } catch (error) {
      if (error instanceof FetchWrapperError) {
        setError(error.code === '403' ? 'Incorrect password' : error.message);
      } else {
        setError('Unable to reveal SSN');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    maskedSsn,
    ssnLoaded,
    loading,
    submitting,
    editOpen,
    editValue,
    setEditValue,
    revealOpen,
    revealPassword,
    setRevealPassword,
    revealedSsn,
    error,
    openEdit,
    closeEdit,
    save,
    openReveal,
    closeReveal,
    reveal,
  };
}
